<template>
  <v-form ref="form" v-model="formValid">
    <v-container class="pa-0">
      <v-row>
        <v-col>
          <v-text-field
            label="Street and number *"
            v-model="localValue.street_and_number"
            :rules="[validateNotEmpty]"
            data-testid="ct-locations-street"
          ></v-text-field>
          <v-text-field
            label="City *"
            v-model="localValue.city"
            :rules="[validateNotEmpty]"
            data-testid="ct-locations-city"
          ></v-text-field>
        </v-col>
        <v-col>
          <v-text-field
            label="Postal Code *"
            v-model="localValue.zip_code"
            :rules="[validateNotEmpty]"
            :error-messages="zipStateError ? [zipStateError] : []"
            data-testid="ct-locations-zip"
          >
            <!-- The mismatch comes from the State field too, so it is passed
                 as error-messages (always shown) rather than as a rule (run
                 only when this field changes). The testid is on the message
                 because Vuetify renders it away from the input. -->
            <template #message="{ message }">
              <span data-testid="ct-locations-zip-error">{{ message }}</span>
            </template>
          </v-text-field>

          <v-text-field
            label="Suite, Unit, Building, Floor, etc."
            v-model="localValue.suite_unit_etc"
            data-testid="ct-locations-suite"
          ></v-text-field>
        </v-col>
        <v-col>
          <v-autocomplete
            hide-no-data
            autocomplete="off"
            label="Country *"
            v-model="localValue.country"
            :rules="[validateNotEmpty, validateShippableCountry]"
            data-testid="ct-locations-country"
            item-title="name"
            item-value="code"
            :items="countriesAndCodes"
          ></v-autocomplete>
          <v-autocomplete
            v-if="isUS"
            hide-no-data
            autocomplete="off"
            label="State *"
            v-model="localValue.region"
            :rules="[validateNotEmpty]"
            data-testid="ct-locations-state"
            :items="usStates"
            item-title="name"
            item-value="code"
          ></v-autocomplete>
          <v-text-field
            v-else
            autocomplete="off"
            label="State/Province *"
            v-model="localValue.region"
            :rules="[validateNotEmpty]"
            data-testid="ct-locations-state"
          ></v-text-field>
        </v-col>
      </v-row>
    </v-container>
  </v-form>
</template>

<script>
import rules from "@/mixins/rules";
import { validateShippableCountry } from "@/utils/countryPolicy";
import {
  ZIP_STATE_MISMATCH_MESSAGE,
  zipStateMismatch,
} from "@/utils/zipStatePolicy";

export default {
  name: "CartShippingForm",

  mixins: [rules],

  props: {
    campaign: {
      type: Object,
      required: true,
    },
    modelValue: {
      type: Object,
      required: true,
    },
    shipToShopperAddress: {
      type: Boolean,
      default: false,
    },
    countriesAndCodes: {
      type: Array,
      default: () => [],
    },
    salesTaxLoading: {
      type: Boolean,
      default: false,
    },
  },

  emits: ["update:modelValue", "update:shipToShopperAddress", "form-valid-changed"],

  data() {
    return {
      // Vuetify's own aggregate, kept ONLY so the template's v-model target
      // still exists. It is NOT what this component reports upward -- see
      // `addressValid` for why.
      formValid: false,
    };
  },

  computed: {
    /**
     * Is the address complete and shippable, judged from the VALUES?
     *
     * Deliberately NOT Vuetify's `<v-form>` modelValue. That aggregate is
     * three-state: it stays `null` until every field has been validated, and
     * Vuetify validates a field on BLUR, not on change. The Country and State
     * inputs are `v-autocomplete`s picked from a menu, and a menu pick sets
     * the model without blurring the input -- so those two fields were never
     * validated, the aggregate never left `null`, and this component never
     * told its parent the address was good.
     *
     * The consequence was a dead "Continue to Checkout" button on a fully
     * typed address (reported on the live AdventHealth store): the parent's
     * `shopperLocationFormValid` stayed false, so `locationPopulated` and
     * then `placeOrderButtonDisabled` stayed false/true forever.
     *
     * This computed applies the SAME rules the fields declare -- required on
     * street/city/zip/country/region, plus the shared US-only
     * `validateShippableCountry` -- so the button and the visible field
     * errors cannot disagree. It reads values, so it settles as soon as the
     * shopper has entered them, with or without a blur.
     */
    addressValid() {
      const value = this.localValue || {};
      const filled = (field) =>
        this.validateNotEmpty(value[field]) === true;

      const requiredFilled = [
        "street_and_number",
        "city",
        "zip_code",
        "country",
        "region",
      ].every(filled);

      // `suite_unit_etc` is optional -- it carries no rule on the field
      // either, and requiring it here would gate checkout on a field the
      // shopper is told is optional.
      return (
        requiredFilled &&
        validateShippableCountry(value.country) === true &&
        !this.zipStateError
      );
    },

    /**
     * The ZIP clearly is not in the chosen State (NY with a California ZIP),
     * or null (#514). Also makes the address INVALID, so the parent quotes
     * nothing for it. A UX hint: the server refuses the same pair.
     */
    zipStateError() {
      const value = this.localValue || {};
      return zipStateMismatch(value.zip_code, value.region, {
        country: value.country,
        stateNames: this.$store.getters.getStates,
      })
        ? ZIP_STATE_MISMATCH_MESSAGE
        : null;
    },

    localValue: {
      get() {
        return this.modelValue;
      },
      set(val) {
        this.$emit("update:modelValue", val);
      },
    },

    isUS() {
      const c = this.localValue.country;
      return !c || c === "US" || c === "United States";
    },

    usStates() {
      const stateMap = this.$store.getters.getStates;
      return Object.entries(stateMap).map(([name, code]) => ({
        name: `${name} (${code})`,
        code,
      }));
    },
  },

  watch: {
    // Report the VALUE-derived verdict, not Vuetify's blur-gated aggregate.
    // `immediate` so the parent gets a definite boolean on mount instead of
    // inheriting its own `false` default by accident -- a prefilled saved
    // address is valid the moment it renders and must say so.
    addressValid: {
      immediate: true,
      handler(newVal) {
        this.$emit("form-valid-changed", newVal);
      },
    },

    // Emit changes to individual fields so the parent stays in sync
    "localValue.street_and_number"() {
      this.$emit("update:modelValue", { ...this.localValue });
    },
    "localValue.city"() {
      this.$emit("update:modelValue", { ...this.localValue });
    },
    "localValue.zip_code"() {
      this.$emit("update:modelValue", { ...this.localValue });
    },
    "localValue.suite_unit_etc"() {
      this.$emit("update:modelValue", { ...this.localValue });
    },
    "localValue.country"(newVal, oldVal) {
      // Clear the state ONLY on a real country CHANGE -- one resolvable
      // country replaced by a DIFFERENT resolvable country. The old
      // `if (oldVal && newVal !== oldVal)` also fired when `newVal` was
      // null/"" , and `v-autocomplete` sets its model to null whenever the
      // user types in the Country box and leaves it without picking an item.
      // That transient null wiped a State the shopper had already chosen,
      // silently invalidating a form that still LOOKED complete -- the
      // shopper sees a country and no state, and cannot tell why checkout
      // will not proceed.
      //
      // Skipping the clear on a blank `newVal` is safe: a blank country is
      // caught by the field's own required rule, and the state is re-cleared
      // the moment a genuinely different country is picked.
      const changedToADifferentCountry =
        !!oldVal && !!newVal && newVal !== oldVal;
      if (changedToADifferentCountry) {
        this.localValue.region = "";
      }
      this.$emit("update:modelValue", { ...this.localValue });
    },
    "localValue.region"() {
      this.$emit("update:modelValue", { ...this.localValue });
    },
  },

  methods: {
    // US-only ship-to (MVP launch constraint). Exposed from
    // ``@/utils/countryPolicy`` so the browser tells the shopper at ADDRESS
    // ENTRY, rather than letting them reach checkout and be refused there.
    // This is a UX HINT -- the authoritative gate is server-side at order
    // creation (api/src/shipping/country_policy.py). Widen the allow-list in
    // BOTH places when a second country ships.
    validateShippableCountry,

    async validate() {
      const { valid } = await this.$refs.form.validate();
      return valid;
    },
  },
};
</script>
